import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useComposerAutocomplete, type Composer } from './useComposerAutocomplete'
import type { SearchComposersResponse } from '@brent/shared/types'

export interface ComposerAutocompleteResult {
  composers: Composer[]
  isLoading: boolean
  error: Error | null
  source: 'static' | 'lambda' | null
}

/**
 * Enhanced composer autocomplete hook with Lambda fallback
 *
 * Tries static file first (fast), falls back to Lambda + Wikidata for rare composers.
 * Pass a searchFn to enable the Lambda fallback; without it only static results are used.
 *
 * @param searchTerm - Search term from user input
 * @param searchFn - Optional portal API function for Lambda fallback
 * @param limit - Max results to return (default: 10)
 * @returns Composers from static file or Lambda, with loading/error states
 */
export const useComposerAutocompleteWithFallback = (
  searchTerm: string,
  searchFn?: (term: string, limit: number) => Promise<SearchComposersResponse>,
  limit: number = 10,
): ComposerAutocompleteResult => {
  // First try static dataset (instant)
  const staticResults = useComposerAutocomplete(searchTerm, limit)

  // Track whether we need to try Lambda fallback
  const [shouldFallback, setShouldFallback] = useState(false)

  // Normalize function to remove accents for matching
  const normalize = (str: string) =>
    str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

  // Check if any static result closely matches the search term (substring match).
  // Fuzzy search may return loosely related results, so we verify with a
  // substring check before skipping the Lambda fallback.
  const hasGoodStaticMatch = staticResults.some((composer) => {
    return normalize(composer.name).includes(normalize(searchTerm))
  })

  // Determine if we should query Lambda
  // Query if:
  // - Search term is at least 2 characters
  // - No static results, OR static results are only fuzzy (not substring) matches
  useEffect(() => {
    if (searchTerm.length >= 2 && !hasGoodStaticMatch) {
      // Delay fallback slightly to avoid hammering API while user is typing
      const timer = setTimeout(() => {
        setShouldFallback(true)
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setShouldFallback(false)
    }
  }, [searchTerm, hasGoodStaticMatch])

  // Query Lambda API for Wikidata search (only if needed and searchFn provided)
  const {
    data: lambdaData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['composer-search-lambda', searchTerm],
    queryFn: () => searchFn!(searchTerm, limit),
    enabled: shouldFallback && searchTerm.length >= 2 && !!searchFn,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1, // Only retry once for Lambda calls
  })

  // Determine which results to return and from what source
  if (hasGoodStaticMatch) {
    return {
      composers: staticResults,
      isLoading: false,
      error: null,
      source: 'static',
    }
  }

  if (shouldFallback) {
    // Convert Lambda API response to Composer format
    const lambdaComposers: Composer[] =
      lambdaData?.composers.map((c) => ({
        id: c.wikidataId,
        name: c.name,
        birth: c.birthYear,
        death: c.deathYear,
        searchTerms: [],
        popularity: 50, // Default popularity for Lambda results
      })) || []

    return {
      composers: lambdaComposers,
      isLoading,
      error: error as Error | null,
      source: lambdaComposers.length > 0 ? 'lambda' : null,
    }
  }

  return {
    composers: [],
    isLoading: false,
    error: null,
    source: null,
  }
}
